import React from 'react'
import styles from './FiltersPopover.scss'
import { formatRangeLabel } from 'features/search/utils'
import { Popover } from 'common/styleguide/Popover'
import { RangeInput } from 'common/styleguide/RangeInput'
import { CTA } from 'common/styleguide/CTA'
import { SEARCH_MAX, LENGTH_MAX, ELEVATION_MAX } from 'lib/api/search'
import { Msg, fm, commonUnits, commonVerbs, commonMetrics } from 'lib/localization'
import { FormattedMessage as T, defineMessages } from 'react-intl'

const messages = defineMessages({
  routesOnly: {
    id: 'FiltersPopover.routesOnly',
    defaultMessage: 'Routes only'
  },
  allTRoutes: {
    id: 'FiltersPopover.allTRoutes',
    defaultMessage: 'All routes and rides'
  }
})

export class FiltersPopover extends React.PureComponent {
  constructor (props) {
    super(props)
    const { query } = this.props

    this.state = query.toJS()
  }

  startLocationChanged = (location) => {
    const stateUpdate = {
      start_location: location
    }

    // If start_location is blank, also blank out start_distance
    const locationAndRange = (!location || location === '') ? Object.assign(stateUpdate, { start_distance: SEARCH_MAX }) : stateUpdate

    this.setState(locationAndRange)
  }

  applyFilters = () => {
    const { query, doSearch, onRequestClose } = this.props

    const newQuery = query.merge(this.state).merge({ sort_by: '' }) // We always want to sort by relevance once they apply some filters.
    doSearch(newQuery)
    onRequestClose()
  }

  onStartDistanceChange = (value) => {
    this.setState({ start_distance: value })
  }

  getStartDistanceLabel = () => {
    const { metricUnits, intl } = this.props
    const unitLabel = fm(intl, metricUnits ? commonUnits.kilometers : commonUnits.miles, { count: 0 })

    return (
      <div className={styles.rangeLabel}>
        {this.state.start_distance === SEARCH_MAX && <T id='FiltersPopover.anywhere' defaultMessage='Anywhere' />}
        {this.state.start_distance !== SEARCH_MAX && `${this.state.start_distance} ${unitLabel}`}
      </div>
    )
  }

  getLengthValue = () => {
    return {
      min: this.state.length_min,
      max: this.state.length_max
    }
  }

  onLengthChange = (value) => {
    this.setState({
      length_min: value.min,
      length_max: value.max
    })
  }

  getLengthLabel = () => {
    const { metricUnits, intl } = this.props
    const unitLabel = fm(intl, metricUnits ? commonUnits.kilometers : commonUnits.miles, { count: 0 })

    return (
      <div className={styles.rangeLabel}>
        {formatRangeLabel(this.state.length_min, this.state.length_max, 0, LENGTH_MAX, fm(intl, commonMetrics.length), unitLabel)}
      </div>
    )
  }

  getElevationValue = () => {
    return {
      min: this.state.elevation_min,
      max: this.state.elevation_max
    }
  }

  onElevationChange = (value) => {
    this.setState({
      elevation_min: value.min,
      elevation_max: value.max
    })
  }

  getElevationLabel = () => {
    const { metricUnits, intl } = this.props
    const unitLabel = fm(intl, metricUnits ? commonUnits.meters : commonUnits.feet, { count: 0 })

    return (
      <div className={styles.rangeLabel}>
        {formatRangeLabel(this.state.elevation_min, this.state.elevation_max, 0, ELEVATION_MAX, fm(intl, commonMetrics.elevation), unitLabel)}
      </div>
    )
  }

  onLookInChange = e => {
    e.target.value === 'true'
      ? this.setState({ trips: false })
      : this.setState({ trips: 1 }) // could also be true, but the default search uses `1`
  }

  render () {
    const { target, onRequestClose, showLookIn, intl } = this.props

    return (
      <Popover
        noPadding
        placement='bottom'
        maxWidth='90vw'
        intl={intl}
        rootClose={false}
        onRequestClose={onRequestClose}
        target={target}>
        <form className={styles.FiltersPopover} onSubmit={this.applyFilters}>
          <div className={styles.filterForm}>
            {showLookIn && <>
              <label htmlFor='look_in'>
                <T id='FiltersPopover.lookIn' defaultMessage='Look in' />
              </label>
              <select className={styles.overwriteLegacySelect} defaultValue={!this.state.trips} onChange={this.onLookInChange}>
                <option value={false}>{intl.formatMessage(messages.allTRoutes)}</option>
                <option value>{intl.formatMessage(messages.routesOnly)}</option>
              </select>
            </>}
            <label htmlFor='start_location'>
              <T id='FiltersPopover.showNear' defaultMessage='Show near' />
            </label>
            <input type='text' name='start_location' className={styles.showNear} value={this.state.start_location} onChange={(e) => this.startLocationChanged(e.target.value)} />
            <div className={styles.filterInput}>
              <T id='FiltersPopover.searchRange' defaultMessage='Search Range' />
              <RangeInput
                disabled={!this.state.start_location || this.state.start_location === ''}
                minValue={0}
                maxValue={SEARCH_MAX}
                value={this.state.start_distance}
                onChange={this.onStartDistanceChange} />
              {this.getStartDistanceLabel()}
            </div>
            <div className={styles.filterInput}>
              <T id='FiltersPopover.length' defaultMessage='Length' />
              <RangeInput
                minValue={0}
                maxValue={LENGTH_MAX}
                value={this.getLengthValue()}
                step={10}
                onChange={this.onLengthChange} />
              {this.getLengthLabel()}
            </div>
            <div className={styles.filterInput}>
              <Msg m={commonMetrics.elevation} />
              <RangeInput
                minValue={0}
                maxValue={ELEVATION_MAX}
                value={this.getElevationValue()}
                step={100}
                onChange={this.onElevationChange} />
              {this.getElevationLabel()}
            </div>
          </div>
          <div className={styles.footer}>
            <CTA inline small link margins={false} onClick={onRequestClose}><Msg capitalized m={commonVerbs.cancel} /></CTA>
            <CTA inline primary small margins={false} onClick={this.applyFilters}><Msg capitalized m={commonVerbs.apply} /></CTA>
          </div>
        </form>
      </Popover>
    )
  }
}
